import { Button, Dropdown, Heading } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import type { Platform } from '../../../context/env-context';
import { I18N_COMPONENTS } from '../constants';
import { findOptions, findVariantImage, resolveOptionName } from '../product-metadata';
import type { Product } from '../types/product';
import { DetailGrid } from './detail-grid';
import { JsonBlock } from './json-block';
import { ProductImage } from './product-image';

export function ProductVariantsCard({
  product,
  selectedSku,
  onSelect,
  platform,
}: {
  product: Product;
  selectedSku: string;
  onSelect: (sku: string) => void;
  platform?: Platform;
}) {
  const selectedVariant = product.variants.find((v) => v.identifiers.sku === selectedSku);
  const isMultiVariant = product.variants.length > 1;
  const [showJson, setShowJson] = useState<boolean>(false);

  if (!selectedVariant) {
    return null;
  }

  const variantImage = findVariantImage(selectedVariant, platform);

  const optionDetails = selectedVariant.option_values.reduce(
    (acc: Record<string, string>, optionValue) => {
      Object.assign(acc, resolveOptionName(product, selectedVariant, optionValue));
      return acc;
    },
    {},
  );

  const details: Record<string, string> = {
    [t(`${I18N_COMPONENTS}.variants_card.sku_label`)]: selectedVariant.identifiers.sku,
    ...optionDetails,
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Heading style="sm" level="h2">
          {t(
            isMultiVariant
              ? `${I18N_COMPONENTS}.variants_card.title_many`
              : `${I18N_COMPONENTS}.variants_card.title_one`,
          )}
        </Heading>
        <Button variant="secondary" size="xs" onClick={() => setShowJson((prev) => !prev)}>
          {showJson
            ? t(`${I18N_COMPONENTS}.variants_card.hide_raw_data`)
            : t(`${I18N_COMPONENTS}.variants_card.show_raw_data`)}
        </Button>
      </div>

      {isMultiVariant && (
        <Dropdown
          value={selectedSku}
          onChange={onSelect}
          options={product.variants.map((variant) => ({
            value: variant.identifiers.sku,
            label: `${findOptions(variant)} - ${variant.identifiers.sku}`,
          }))}
        />
      )}

      {showJson ? (
        <JsonBlock value={selectedVariant} show />
      ) : (
        <div className="flex justify-start gap-x-6 rounded-xl border border-gray-200 bg-white p-5 px-6 shadow">
          <ProductImage src={variantImage} size="lg" />
          <DetailGrid details={details} />
        </div>
      )}
    </section>
  );
}
